import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./MetadataCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`meta-reveal${visible ? " meta-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M4 12l6 6L20 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TargetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const LayersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l9 5-9 5-9-5 9-5z" strokeLinejoin="round" />
    <path d="M3 13l9 5 9-5" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" strokeLinejoin="round" />
  </svg>
);

const QuoteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 8c-1.8 1-3 2.8-3 5a3 3 0 003 3M17 8c-1.8 1-3 2.8-3 5a3 3 0 003 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BraceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 3c-2 0-3 1-3 3v4c0 1-1 2-2 2 1 0 2 1 2 2v4c0 2 1 3 3 3M16 3c2 0 3 1 3 3v4c0 1 1 2 2 2-1 0-2 1-2 2v4c0 2-1 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 5.5A2.5 2.5 0 016.5 3H12v18H6.5A2.5 2.5 0 014 18.5v-13z" strokeLinejoin="round" />
    <path d="M20 5.5A2.5 2.5 0 0017.5 3H12v18h5.5a2.5 2.5 0 002.5-2.5v-13z" strokeLinejoin="round" />
  </svg>
);

const META_FORMATS = [
  { badge: "APA", title: "APA Citation Generator", desc: "Use the APA metadata citation generator when your paper follows APA style, especially in education, psychology, business, health sciences, and the social sciences.", tags: ["APA citation generator", "Metadata citation generator online"] },
  { badge: "HARV", title: "Harvard Citation Generator", desc: "Use the Harvard format when your university or department asks for Harvard referencing, common in UK and international academic writing.", tags: ["Harvard citation generator", "Harvard metadata citation"] },
  { badge: "MLA", title: "MLA Citation Generator", desc: "Use MLA format when your assignment needs the metadata arranged for English, literature, or the arts.", tags: ["MLA citation generator", "MLA metadata citation"] },
  { badge: "CMS", title: "Chicago Citation Generator", desc: "Use Chicago style when your paper follows The Chicago Manual of Style, common in history, literature, and the arts.", tags: ["Chicago citation generator", "Chicago metadata citation"] },
  { badge: "BIB", title: "BibTeX Citation Generator", desc: "Use BibTeX when you are writing in LaTeX or working with tools such as Overleaf, and need the metadata exported as a reference entry.", tags: ["Generate BibTeX citation", "BibTeX metadata generator"] },
  { badge: "META", title: "Metadata Fields Reference", desc: "See the structured metadata behind a source, type, title, date, author, publisher, and identifier, before it gets formatted into any citation style.", tags: ["Metadata citation lookup", "Free metadata citation generator online"] },
];

const META_STEPS = [
  { title: "Choose Your Citation Style", desc: "Select APA, MLA, Harvard, Chicago, or BibTeX before you generate the citation." },
  { title: "Select the Identifier Type", desc: "Choose URL, DOI, ISBN, PubMed ID, or title, depending on what you have for the source." },
  { title: "Paste the Source Details", desc: "Enter the link, identifier, or title you want to cite." },
  { title: "Review the Metadata", desc: "Check the author, title, date, publisher, and container the tool has pulled from the source." },
  { title: "Copy the Citation", desc: "Copy the full reference, plus the in-text citation, into your paper or reference list." },
];

const META_SOURCE_TYPES = [
  { kicker: "Website", title: "Website Citation Generator", desc: "Reads the page title, author or organization, publish date, and URL for webpages, blogs, and online articles." },
  { kicker: "Book", title: "Book Citation Generator", desc: "Reads the title, author, edition, publisher, and ISBN for print books, ebooks, and textbook chapters." },
  { kicker: "Journal Article", title: "Journal Article Citation Generator", desc: "Reads the article title, authors, journal name, volume, issue, and DOI for scholarly and open-access papers." },
  { kicker: "Video", title: "YouTube and Video Citation Generator", desc: "Reads the video title, channel or creator, upload date, and URL for lectures, webinars, and interviews." },
  { kicker: "Newspaper", title: "Newspaper Citation Generator", desc: "Reads the headline, byline, publication name, and date for online and print newspaper articles." },
  { kicker: "Report", title: "Report Citation Generator", desc: "Reads the title, author or agency, publication date, and identifier for research reports, white papers, and government documents." },
];

const META_EXAMPLES = [
  {
    tag: "Journal Article · APA",
    ref: <>Smith, J., &amp; Lee, R. (2023). Understanding metadata in academic research. <em>Journal of Information Science</em>, 31(5), 109-115. https://doi.org/10.1000/example</>,
    intext: "(Smith & Lee, 2023)",
  },
  {
    tag: "Website · MLA",
    ref: <>ResearchPal. "How Metadata Shapes a Better Citation." <em>ResearchPal Blog</em>, 2026, researchpal.co.</>,
    intext: "(ResearchPal)",
  },
];

const META_WHY_ITEMS = [
  { icon: <TargetIcon />, title: "Metadata-Accurate Formatting", desc: "Citations are built from the source's actual metadata, not guesswork, so titles, dates, and authors stay accurate." },
  { icon: <LayersIcon />, title: "Multi-Style Output", desc: "The same metadata generates APA, MLA, Chicago, Harvard, or BibTeX without re-entering a single field." },
  { icon: <EditIcon />, title: "Manual Editing", desc: "Fix or add missing metadata by hand when a source is incomplete or unusual." },
  { icon: <QuoteIcon />, title: "In-Text & Full Reference", desc: "Generates the in-text citation together with the full reference every time." },
  { icon: <BraceIcon />, title: "BibTeX Output", desc: "Built for LaTeX users and Overleaf projects that need a BibTeX entry, not a formatted paragraph." },
  { icon: <BookIcon />, title: "Bibliography Support", desc: "Collects metadata citations for longer, multi-source reference lists and bibliographies." },
];

const META_PLAN_COMPARISON = [
  { feature: "Generate APA citations", free: true, premium: true },
  { feature: "Generate MLA citations", free: true, premium: true },
  { feature: "Generate Chicago citations", free: true, premium: true },
  { feature: "Read source metadata automatically", free: true, premium: true },
  { feature: "Copy citations", free: true, premium: true },
  { feature: "Save bibliography lists", free: "limited", premium: "Unlimited" },
  { feature: "Export to Word or Google Docs", free: "limited", premium: "Unlimited" },
  { feature: "Bulk metadata import", free: null, premium: true },
];

const META_FAQS = [
  { q: "What is a metadata citation generator?", a: "A metadata citation generator reads the details attached to a source, its title, author, publication date, publisher, and identifier, and arranges them into a formatted citation. Instead of typing every field by hand, you paste a link or identifier and the tool builds the reference from the metadata it finds." },
  { q: "How do I generate a metadata citation?", a: "Choose a citation style, select whether you have a URL, DOI, ISBN, or PubMed ID, then paste the source. The generator reads the metadata and produces a full reference along with the matching in-text citation." },
  { q: "Is this metadata citation generator free?", a: "Yes. Pasting a source and generating a metadata citation online is free, with no account required. A premium plan adds saved bibliography lists, exports, and bulk import for longer projects." },
  { q: "Which citation styles does the metadata citation generator support?", a: "APA, MLA, Harvard, Chicago, and BibTeX. Every style pulls from the same source metadata, so you can switch styles without re-entering any details." },
  { q: "What can I paste into the generator?", a: "A URL, a DOI, an ISBN, a PubMed ID, or a plain title. The generator matches the identifier to its metadata and builds the citation from there." },
  { q: "Can I create a metadata citation without a DOI or ISBN?", a: "Yes. If a source has no identifier, paste the title or the page URL instead. The generator can still read enough metadata, title, author, date, to build the citation, though you may need to add a missing field by hand." },
  { q: "Does the tool show the raw metadata behind a citation?", a: "Yes. The Metadata view lists the structured fields, type, title, date, publisher, and identifier, behind a source before they are arranged into any citation style." },
  { q: "Can I edit the metadata before generating the citation?", a: "Yes. Once the metadata loads, you can correct or add fields manually before generating the final citation, useful when a source is missing details or lists them inconsistently." },
  { q: "Does the metadata citation generator work for books and journal articles?", a: "Yes. It reads book metadata, title, author, edition, publisher, ISBN, and journal metadata, article title, authors, volume, issue, and DOI, and formats either into the style you choose." },
  { q: "Can I generate a BibTeX entry from source metadata?", a: "Yes. Select BibTeX as the format and the same source metadata is exported as a BibTeX entry, ready for LaTeX or Overleaf." },
  { q: "Does the generator create in-text citations too?", a: "Yes. Every generated reference comes with its matching in-text citation, built from the same metadata, so both stay consistent." },
  { q: "Should I double-check the metadata before submitting my paper?", a: "Yes. Metadata pulled automatically is usually accurate, but sources sometimes list incomplete or outdated details. Reviewing the author, date, and title against the original source takes a moment and avoids errors in your reference list." },
  { q: "Can I save a list of metadata citations for a longer paper?", a: "Saving bibliography lists is part of the premium plan. The free version generates and copies citations one at a time without limits on how many you create." },
  { q: "Is my source information kept private?", a: "Yes. The details you paste are used only to look up and generate the citation, and are not shared or published." },
  { q: "Can I export metadata citations to Word or Google Docs?", a: "Exporting directly to Word or Google Docs is a premium feature. On the free plan, you can copy the generated citation and paste it into any document." },
];

const FAQ_BATCH = 5;

const MetaFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [activeIdx, setActiveIdx] = useState(null);

  const onClick = (idx) => {
    setActiveIdx((prev) => (prev === idx ? null : idx));
    // Reference design reveals the next batch on every click, whether opening or closing.
    setRevealed((prev) => (prev < META_FAQS.length ? Math.min(prev + FAQ_BATCH, META_FAQS.length) : prev));
  };

  const visible = META_FAQS.slice(0, revealed);
  const done = revealed >= META_FAQS.length;

  return (
    <>
      <div className="meta-faq-list">
        {visible.map((item, idx) => {
          const active = activeIdx === idx;
          return (
            <div
              className={`meta-faq-item${active ? " meta-active" : ""}`}
              key={item.q}
              onClick={() => onClick(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(idx); }}
            >
              <div className="meta-faq-q">
                <span><span className="meta-num">{String(idx + 1).padStart(2, "0")}</span>{item.q}</span>
                <span className="meta-faq-chevron">
                  <svg width="11" height="7" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
              </div>
              <div className="meta-faq-a">{item.a}</div>
            </div>
          );
        })}
      </div>
      <div className="meta-faq-more-wrap">
        <span className="meta-faq-hint">{done ? "That is every question we have for now." : "Click a question to reveal the next five."}</span>
      </div>
    </>
  );
};

const MetadataCitationContent = () => {
  return (
    <div className="meta-lp">
      <section className="meta-intro">
        <Reveal as="h1">Free Metadata Citation Generator: Pull Source Details, Cite in Any Style</Reveal>
        <Reveal as="p">Every source carries metadata: an author, a title, a date, a publisher, an identifier. A metadata citation generator reads those details and arranges them into a properly formatted reference, so you do not have to search through style manuals or count commas by hand. Paste a link, a DOI, an ISBN, or a PubMed ID, and let the tool read the metadata straight from the source, then choose APA, MLA, Harvard, Chicago, or BibTeX to build the full reference, the in-text citation, and the bibliography entry from it.</Reveal>
        <Reveal className="meta-cta-row">
          <a href="#citation-tool" className="meta-btn meta-btn-solid">Generate My Metadata Citation Free</a>
          <a href="#meta-formats" className="meta-link-underline">View Citation Styles</a>
        </Reveal>
        <Reveal as="div" className="meta-chip-row">
          <span className="meta-chip"><CheckIcon />Reads source metadata automatically</span>
          <span className="meta-chip"><CheckIcon />Supports major citation styles</span>
          <span className="meta-chip"><CheckIcon />Builds in-text citations</span>
          <span className="meta-chip"><CheckIcon />Saves time on formatting</span>
          <span className="meta-chip"><CheckIcon />Edit fields manually</span>
          <span className="meta-chip"><CheckIcon />Copy, save, or export</span>
        </Reveal>
      </section>

      <section className="meta-band meta-mint" id="meta-basics">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">The Basics</span>
            <h2>What Is a Metadata Citation Generator?</h2>
          </Reveal>
          <Reveal className="meta-quote-card">
            <p>"A metadata citation generator reads the details already attached to a source, its title, author, date, publisher, and identifier, and arranges them into a citation, so you do not have to transcribe every field by hand."</p>
          </Reveal>
          <div className="meta-basics-grid">
            <Reveal className="meta-basics-card">
              <h3>How It Works</h3>
              <p>Paste a URL, DOI, ISBN, or PubMed ID. The tool reads the metadata attached to that source, title, author, publication date, publisher, and container, then arranges it into the citation style you choose.</p>
            </Reveal>
            <Reveal className="meta-basics-card">
              <h3>Why It Helps</h3>
              <p>A metadata citation generator does not guess at formatting. It works from the metadata actually attached to the source, which keeps entries consistent, cuts down on transcription mistakes, and gives proper credit to where information came from.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="meta-band" id="meta-formats">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">Pick Your Format</span>
            <h2>Which Citation Styles Can a Metadata Citation Generator Build?</h2>
            <p>The same source metadata can be arranged into any of these styles, depending on what your paper, journal, or department asks for.</p>
          </Reveal>
          <div className="meta-format-grid">
            {META_FORMATS.map((f) => (
              <Reveal key={f.badge} className="meta-format-card">
                <div className="meta-format-head"><span className="meta-format-badge">{f.badge}</span><h3>{f.title}</h3></div>
                <p>{f.desc}</p>
                <div className="meta-tag-row">{f.tags.map((t) => <span className="meta-tag" key={t}>{t}</span>)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="meta-band meta-mint" id="meta-how">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">Find Simple Steps</span>
            <h2>How to Generate a Metadata Citation</h2>
          </Reveal>
          <div className="meta-steps-list">
            {META_STEPS.map((step, idx) => (
              <Reveal className="meta-step-row" key={step.title}>
                <div className="meta-step-num">{idx + 1}</div>
                <div className="meta-step-body"><h3>{step.title}</h3><p>{step.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="meta-band">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">By Source</span>
            <h2>Generate a Metadata Citation by Source Type</h2>
            <p>Different sources carry different metadata. Here is what the generator reads for each type.</p>
          </Reveal>
          <div className="meta-source-grid">
            {META_SOURCE_TYPES.map((item) => (
              <Reveal key={item.kicker} className="meta-source-card">
                <div className="meta-kicker">{item.kicker}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="meta-band meta-mint" id="meta-examples">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">See It In Action</span>
            <h2>Example Metadata Citations</h2>
          </Reveal>
          <div className="meta-example-grid">
            {META_EXAMPLES.map((ex) => (
              <Reveal key={ex.tag} className="meta-example-card">
                <div className="meta-example-tag">{ex.tag}</div>
                <div className="meta-example-body">{ex.ref}</div>
                <div className="meta-example-intext">In-text citation: {ex.intext}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="meta-band">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">The Payoff</span>
            <h2>Why Use a Metadata Citation Generator?</h2>
          </Reveal>
          <div className="meta-why-grid">
            {META_WHY_ITEMS.map((w) => (
              <Reveal key={w.title} className="meta-why-card">
                <div className="meta-why-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="meta-band meta-mint" id="meta-plans">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">Compare Plans</span>
            <h2>Free and Premium Metadata Citation Features</h2>
          </Reveal>
          <Reveal className="meta-plans-table">
            <div className="meta-plans-row meta-head"><span>Feature</span><span>Free Version</span><span>Premium Version</span></div>
            {META_PLAN_COMPARISON.map((row) => (
              <div className="meta-plans-row" key={row.feature}>
                <span>{row.feature}</span>
                <span className={row.free === true ? "meta-badge-yes" : row.free === "limited" ? "meta-badge-limited" : "meta-badge-dash"}>
                  {row.free === true ? "✓" : row.free === "limited" ? "Limited" : "—"}
                </span>
                <span className={row.premium === true ? "meta-badge-yes" : "meta-badge-unlimited"}>
                  {row.premium === true ? "✓" : row.premium}
                </span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="meta-band" id="meta-ai-research-assistant">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">AI Research Assistant</span>
            <h2>AI Research Assistant for Research Source Metadata</h2>
          </Reveal>
          <div className="meta-basics-grid">
            <Reveal className="meta-basics-card">
              <p>Accurate source information is the foundation of a reliable citation. Author names, publication dates, titles, identifiers and publication details all influence how a source is referenced.</p>
              <p>ResearchPal's citation workflow can retrieve available metadata from inputs such as paper titles, DOIs, URLs, ArXiv IDs and PubMed IDs. The Metadata view provides a way to inspect the structured information associated with a source before using it in your research workflow.</p>
              <p>The ResearchPal AI Research Assistant adds another layer by helping you understand the source itself. Instead of looking at metadata in isolation, you can investigate what the paper is about, discuss its relevance, ask research questions and determine whether the source is useful for your work.</p>
            </Reveal>
            <Reveal className="meta-basics-card">
              <h3>Use the AI Research Assistant With Source Metadata</h3>
              <p>You can ask the Agent to help you:</p>
              <ul className="meta-ai-list">
                <li>Understand what a research paper is about</li>
                <li>Determine whether a source is relevant to your topic</li>
                <li>Clarify unfamiliar academic terminology</li>
                <li>Discuss a paper's findings and contribution</li>
                <li>Explore questions raised by the source</li>
                <li>Decide what other research you should investigate</li>
              </ul>
            </Reveal>
          </div>
          <Reveal className="meta-ai-cta">
            <Link to="/ai-agent" className="meta-btn meta-btn-solid">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="meta-band" id="meta-free-tools">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">More Free Tools</span>
            <h2>Explore ResearchPal's Free Tools</h2>
          </Reveal>

          <div className="meta-free-tools-group">
            <h3 className="meta-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="meta-source-grid">
              <Link className="meta-free-tool-card" to="/paraphraser">
                <div><h3>Paraphraser</h3><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/ai-agent">
                <div><h3>AI Agent</h3><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/search">
                <div><h3>Search Papers</h3><p>Find and explore academic papers across sources.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/essay-writer">
                <div><h3>Essay Writer</h3><p>Draft structured essays with AI assistance.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/literature-review">
                <div><h3>Literature Review</h3><p>Summarize and organize sources for your review.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/paper-insights">
                <div><h3>Paper Insights</h3><p>Get quick, structured insights from any paper.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="meta-free-tools-group">
            <h3 className="meta-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="meta-source-grid">
              <Link className="meta-free-tool-card" to="/citation-generator/apa">
                <div><h3>APA Citation Generator</h3><p>Format references in APA style instantly.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/citation-generator/harvard">
                <div><h3>Harvard Citation Generator</h3><p>Create author-date Harvard references.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/citation-generator/mla">
                <div><h3>MLA Citation Generator</h3><p>Generate MLA in-text and Works Cited entries.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/citation-generator/chicago">
                <div><h3>Chicago Citation Generator</h3><p>Build Notes-Bibliography or Author-Date citations.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
              <Link className="meta-free-tool-card" to="/citation-generator/bibtex">
                <div><h3>BibTeX Generator</h3><p>Get ready-to-use BibTeX entries for LaTeX.</p></div>
                <span className="meta-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="meta-cta-band">
        <span className="meta-eyebrow">Ready to Cite</span>
        <Reveal as="h2">Ready to Generate Your Metadata Citation?</Reveal>
        <Reveal as="p">Paste a link, DOI, ISBN, or title and get a formatted citation in seconds, built from the source's own metadata, with no sign-up required to try.</Reveal>
        <Reveal as="div">
          <a href="#citation-tool" className="meta-btn meta-btn-solid">Generate My Metadata Citation Free</a>
        </Reveal>
      </section>

      <section className="meta-band" id="meta-faq">
        <div className="meta-wrap">
          <Reveal className="meta-sec-head">
            <span className="meta-eyebrow">Shortcuts</span>
            <h2>Frequently Asked Questions</h2>
          </Reveal>
          <MetaFaqList />
        </div>
      </section>
    </div>
  );
};

export default MetadataCitationContent;
